import { useCallback, useEffect, useRef, useState } from 'react';

import { Badge, Button, Card } from '@ds/core';
import { Alert } from '@ds/feedback';
import { dataSimples } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ResumoDoPaciente } from '@/data/tipos';

/**
 * O resumo de TODAS as consultas, na coluna direita da aba Prontuário (0171).
 *
 * Pedido de 22/09/2026: uma nota-resumo do histórico, feita das notas das consultas, que
 * se atualiza quando entra consulta nova. A matéria-prima são os resumos que a VecchIA
 * escreveu e o profissional APROVOU em cada atendimento (0155) — texto que alguém já
 * conferiu — mais as observações do atendimento.
 *
 * **Três coisas que este card nunca esconde**, porque escondê-las tornaria o resumo
 * perigoso em vez de útil:
 *
 * 1. **De quantas consultas ele foi feito, e até quando.** Resumo sem alcance engana: o
 *    de três meses atrás parece o de hoje para quem vai atender em cinco minutos.
 * 2. **Que há consulta nova depois dele.** O gatilho do banco marca `desatualizado`
 *    assim que um atendimento é concluído ou uma nota é aprovada; aqui isso vira um aviso
 *    e um botão, não um silêncio.
 * 3. **Que foi escrito por IA e não é prontuário.** A ficha e a nota aprovada continuam
 *    sendo a verdade clínica (regra 8).
 *
 * **Automático, por decisão do usuário (22/09/2026).** O resumo se refaz sozinho quando
 * não existe ou quando o gatilho do banco o marcou desatualizado — é o comportamento
 * pedido: "cada novo registro atualiza o resumo, sempre exibir". Custa uma chamada de IA
 * e entra no teto da clínica, então há duas travas contra desperdício: só refaz quando
 * falta ou envelheceu (abrir a ficha dez vezes no mesmo dia gera uma vez só), e uma falha
 * não vira nova tentativa a cada render — o botão fica para quem quiser insistir.
 */
export function ResumoDasConsultas({ contatoId }: { contatoId: string }) {
    const aviso = useAviso();
    const [resumo, setResumo] = useState<ResumoDoPaciente | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [gerando, setGerando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const gerar = useCallback(async (automatico = false) => {
        setGerando(true);
        setErro(null);
        try {
            setResumo(await dados().gerarResumoDoPaciente(contatoId));
            /* No automático a pessoa não pediu nada: avisar seria interromper sem motivo. */
            if (!automatico) aviso({ title: 'Resumo atualizado', tone: 'success' });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível gerar o resumo agora.');
        } finally {
            setGerando(false);
        }
    }, [aviso, contatoId]);

    /* O efeito depende SÓ do contato. `gerar` fica num ref de propósito: se ele entrasse
       nas dependências, qualquer render que trocasse a identidade de `aviso` reabriria o
       efeito — e cada reabertura é uma chamada de IA paga. Uma ficha aberta, no máximo
       uma geração. */
    const gerarAgora = useRef(gerar);
    gerarAgora.current = gerar;

    useEffect(() => {
        let vivo = true;
        setCarregando(true);
        void (async () => {
            let guardado: ResumoDoPaciente | null = null;
            try {
                guardado = await dados().resumoDoPaciente(contatoId);
            } catch {
                /* Ler o resumo não pode derrubar a ficha: ele é conforto, não prontuário. */
                guardado = null;
            }
            if (!vivo) return;
            setResumo(guardado);
            setCarregando(false);
            /* Falta ou envelheceu: refaz sozinho. Estando em dia, não gasta nada. */
            if (!guardado?.texto || guardado.desatualizado) await gerarAgora.current(true);
        })();
        return () => { vivo = false; };
    }, [contatoId]);

    const alcance = resumo && resumo.consultas > 0
        ? `${resumo.consultas} ${resumo.consultas === 1 ? 'consulta' : 'consultas'}${resumo.ateEm ? ` · até ${dataSimples(resumo.ateEm.slice(0, 10))}` : ''}`
        : undefined;

    return (
        <Card
            title="Resumo das consultas"
            subtitle={alcance}
            padding={18}
            actions={resumo?.texto ? (
                <Button size="sm" variant="ghost" loading={gerando} onClick={() => { void gerar(); }}>Atualizar</Button>
            ) : undefined}
        >
            {erro && <Alert tone="danger" dismissible={false} title="Não foi possível gerar">{erro}</Alert>}

            {carregando ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>Carregando…</p>
            ) : resumo?.texto ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {resumo.desatualizado && (
                        <Badge tone="warning" dot>Há consulta nova depois deste resumo</Badge>
                    )}
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '20px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                        {resumo.texto}
                    </p>
                    <p style={{ margin: 0, fontSize: 11.5, lineHeight: '16px', color: 'var(--text-tertiary)' }}>
                        Escrito pela VecchIA a partir das notas das consultas. Não substitui a ficha nem a nota aprovada.
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                        {gerando
                            ? 'Escrevendo o resumo a partir das notas das consultas…'
                            : resumo && resumo.consultas === 0 && !resumo.desatualizado
                                ? 'Ainda não há consulta concluída com nota ou observação para resumir.'
                                : erro
                                    ? 'O resumo não pôde ser escrito agora.'
                                    : 'Este paciente ainda não tem um resumo.'}
                    </p>
                    <Button size="sm" loading={gerando} onClick={() => { void gerar(); }}>
                        {erro ? 'Tentar de novo' : 'Gerar resumo'}
                    </Button>
                </div>
            )}
        </Card>
    );
}
